import { gql } from "@apollo/client";
import Link from 'next/link';
import classNames from 'classnames';
import PropTypes from "prop-types";
import { BookmarkMinus, BookmarkCheck } from 'lucide-react';
import { memo } from 'react';

import Heading from "../Heading/Heading";
import FeaturedImage from "../FeaturedImage/FeaturedImage";
import TermLinks from "../TermLinks/TermLinks";
import Button from "../Button/Button";
import { CARD_SIZE } from "@/lib/utils/constants";
import { getStyledTitle } from "@/lib/utils/helpers";
// import ImgBadge from '@/lib/assets/images/badge.svg';
import { createClient } from '@/lib/supabase/component';
import { useSaveStore } from '@/lib/stores/post';

const imageDimensions = {
  default: { width: 308, height: 185 },
  small: { width: 208, height: 208 },
  large: { width: 635, height: 380 },
};

const PostCard = memo(function PostCard(
  {
    title,
    uri,
    categories,
    videoSeries,
    featuredImage,
    featuredImagePriority = false,
    className,
    momHasVideo,
    isFeatured = false, // True to replace term link with featured text
    momHasFeaturedBadge = false, // True to show featured badge. TODO: redo as it may cause 502 error
    variant = 'default',
    databaseId,
    enableSaveButton = false,
    openInNewTab = false,
    linkToComments = false,
  }
) {
  const imageSize = imageDimensions[variant] || imageDimensions.default;
  const isSmallVariant = variant === 'small';

  const supabase = createClient();
  const { unsaveStory } = useSaveStore();

  const handleRemoveSaved = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!enableSaveButton) return;

    if (databaseId) {
      await unsaveStory(supabase, databaseId);
    }
  };

  // Fallback to videoSeries if categories is empty
  const terms = categories;
  // const terms = videoSeries || categories;

  const finalUri = linkToComments ? `${uri}#comments-section-${databaseId}` : uri;

  return (
    <div className={classNames(`group/PostCard`, className)}>
      <Link
        href={finalUri}
        className={classNames(
          'no-underline block relative',
          { 'group-hover/PostCard:opacity-90 transition-opacity': !momHasVideo },
          { 'mom-has-video': momHasVideo },
          { 'float-left mr-4 size-[80px] lg:size-[104px]': isSmallVariant },
          { 'mb-4': !isSmallVariant },
        )}
        aria-label={title}
        {...(openInNewTab && { target: '_blank', rel: 'noopener noreferrer' })}
      >
        <FeaturedImage
          {...imageSize}
          priority={featuredImagePriority}
          image={featuredImage?.node}
        />
        {enableSaveButton && (
          <Button
            variant={'icon'}
            className={`group/Saved absolute -top-[5px] -right-[1px] z-10 mom-link`}
            onClick={handleRemoveSaved}
            aria-label="Remove from saved stories"
            title="Remove from saved stories"
          >
            <BookmarkCheck strokeWidth={1.75} className={`group-hover/Saved:!hidden size-5 !mr-0 [&_path+path]:stroke-[#fff]`} fill='currentColor' />
            <BookmarkMinus strokeWidth={1.75} className={`!hidden group-hover/Saved:!block size-5 !mr-0 [&_path+line]:stroke-[#fff]`} fill='currentColor' />
          </Button>
        )}
        {/* {momHasFeaturedBadge && (
          <span className={classNames(
            `absolute top-0.5 left-0.5`,
          )}>
            <ImgBadge className={`relative z-[2] lucide w-7 h-7`} />
          </span>
        )} */}
      </Link>

      <div className={classNames({
        'mb-2 md:mb-4': !isSmallVariant,
        'mb-1 line-clamp-1 md:line-clamp-none': isSmallVariant
      })}
      >
        {/* {!isFeatured
          ? <TermLinks terms={categories} primaryOnly={true} variant={variant}/>
          : <span className={`mom-pill`}>FEATURED POST</span>
        } */}
        <TermLinks terms={terms} primaryOnly={true} variant={variant} className={`leading-5`} />
      </div>

      <Heading level={'h3'} className={classNames(
        { 'font-sans line-clamp-3 md:line-clamp-4 text-xs lg:text-sm': isSmallVariant },
        { 'text-base md:text-h4 mb-0': !isSmallVariant },
      )}>
        <Link
          href={finalUri}
          title={title}
          {...(openInNewTab && { target: '_blank', rel: 'noopener noreferrer' })}
          className={
            `no-underline group-hover/PostCard:text-navy dark:group-hover/PostCard:text-silver transition-colors`
          }
        >{getStyledTitle({ title })}
        </Link>
      </Heading>
    </div>
  );
});

PostCard.fragments = {
  key: 'PostCardFragment',
  entry: gql`
    ${FeaturedImage.fragments.entry}
    ${TermLinks.fragments.entryCategory}
    fragment PostCardFragment on Post {
      featuredImage {
        ...${FeaturedImage.fragments.key}
      }
      categories {
        ...${TermLinks.fragments.keyCategory}
      }
      momHasVideo
      databaseId
      title
      uri
    }
  `,
  keyVideo: 'VideoCardFragment',
  entryVideo: gql`
    ${FeaturedImage.fragments.entry}
    ${TermLinks.fragments.entryVideo}
    fragment VideoCardFragment on Video {
      featuredImage {
        ...${FeaturedImage.fragments.key}
      }
      databaseId
      title
      uri
      momHasVideo
      videoSeries {
        ...${TermLinks.fragments.keyVideo}
      }
    }
  `
};

PostCard.propTypes = {
  categories: PropTypes.object,
  className: PropTypes.string,
  featuredImage: PropTypes.object,
  featuredImagePriority: PropTypes.bool,
  isFeatured: PropTypes.bool,
  momHasFeaturedBadge: PropTypes.bool,
  momHasVideo: PropTypes.bool,
  variant: PropTypes.oneOf(CARD_SIZE),
  databaseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string.isRequired,
  uri: PropTypes.string.isRequired,
};

export default PostCard;
